<template>
  <ol
    class="checkout-progress"
    :aria-label="$t('title')"
  >
    <li
      v-for="(item, index) in steps"
      :key="index"
      class="checkout-progress__item"
    >
      <div
        v-if="item.isFinal"
        class="checkout-progress__badge is-completed"
      >
        <div class="checkout-progress__number">
          <icon
            type="check"
            :size="0.875"
          />
          <span class="sr-only">{{ $t('completed') }}:</span>
        </div>
        <div class="checkout-progress__name">
          {{ item.name }}
        </div>
      </div>

      <router-link
        v-else-if="item.isDone && item.route"
        :to="{ name: item.route }"
        class="checkout-progress__badge is-completed is-link"
        exact
      >
        <div class="checkout-progress__number">
          <icon
            type="check"
            :size="0.875"
          />
          <span class="sr-only">{{ $t('completed') }}:</span>
        </div>
        <div class="checkout-progress__name">
          {{ item.name }}
        </div>
      </router-link>

      <div
        v-else
        class="checkout-progress__badge"
        :class="{ 'is-active': item.isActive }"
      >
        <div class="checkout-progress__number">
          {{ index + 1 }}<span class="sr-only">.</span>
        </div>
        <div class="checkout-progress__name">
          {{ item.name }}
        </div>
      </div>
    </li>
  </ol>
</template>

<script>
import icon from './icon.vue'

import { GAP_GIFT_CARD, GAP_ALL_DAY } from '../settings.js'

export default {
  name: 'CCheckoutProgress',

  components: {
    icon
  },

  props: {
    reservation: {
      type: Boolean
    }
  },

  computed: {
    steps () {
      const BOOKING_ROUTE_NAME = `booking-${this.$i18n.locale}`
      const GIFTCARDS_ROUTE_NAME = `giftcards-${this.$i18n.locale}`
      const TOURS_ROUTE_NAME = `booking-tours-${this.$i18n.locale}`
      const YULLBE_ROUTE_NAME = 'yullbe'

      const translationKey = this.reservation ? 'reservationSteps' : 'bookingSteps'
      const steps = Object.values(this.$t(translationKey))

      const currentRoute = this.$route.name

      const hasGiftCards = this.$store.state.cart.events.gap === GAP_GIFT_CARD

      // Remove the giftcards step
      if (hasGiftCards || currentRoute === GIFTCARDS_ROUTE_NAME) {
        const bookingIndex = steps.findIndex(step => step.route === BOOKING_ROUTE_NAME)
        steps.splice(bookingIndex, 1)
      } else {
        const giftcardsIndex = steps.findIndex(step => step.route === GIFTCARDS_ROUTE_NAME)
        steps.splice(giftcardsIndex, 1)
      }

      const hasTours = this.$store.state.cart.tours.total_articles ||
        this.$store.state.cart.tickets.gap === GAP_ALL_DAY

      // Remove the guided tours step if the user doesn’t book any tours or
      // if the user is reserving tickets.
      if (!this.reservation && !hasTours && (currentRoute !== TOURS_ROUTE_NAME || currentRoute !== YULLBE_ROUTE_NAME)) {
        const toursIndex = steps.findIndex(step => (step.route === TOURS_ROUTE_NAME || step.route === YULLBE_ROUTE_NAME))
        steps.splice(toursIndex, 1)
      }

      // "Seal" the checkout progress and mark every step as final if the
      // current route is the last step.
      const isFinal = currentRoute.includes('confirmation')

      // This flag is used to mark every step as done until the active one is found.
      let activeRouteFound = false

      return steps.map(step => {
        // this is *quite* the ugly hack to get yullbe routes to match with tours... idc
        const isActive = step.route === currentRoute ||
                         (step.route === TOURS_ROUTE_NAME && currentRoute === YULLBE_ROUTE_NAME)

        if (!activeRouteFound) {
          activeRouteFound = isActive
        }

        return {
          name: step.name,
          route: step.route,
          isActive,
          isDone: !activeRouteFound,
          isFinal
        }
      })
    }
  }
}
</script>

<i18n locale="en">
{
  "title": "Checkout Progress",
  "bookingSteps": [
    {
      "name": "Tickets",
      "route": "booking-en"
    },
    {
      "name": "Gift Cards",
      "route": "giftcards-en"
    },
    {
      "name": "Experiences",
      "route": "booking-tours-en"
    },
    {
      "name": "Overview",
      "route": "booking-overview-en"
    },
    {
      "name": "Personal Information",
      "route": "booking-personal-information-en"
    },
    {
      "name": "Payment"
    },
    {
      "name": "Confirmation",
      "route": "booking-confirmation-en"
    }
  ],
  "reservationSteps": [
    {
      "name": "Tickets",
      "route": "reservation-en"
    },
    {
      "name": "Overview",
      "route": "reservation-overview-en"
    },
    {
      "name": "Personal Information",
      "route": "reservation-personal-information-en"
    },
    {
      "name": "Confirmation",
      "route": "reservation-confirmation-en"
    }
  ],
  "completed": "Step completed"
}
</i18n>

<i18n locale="de">
{
  "title": "Bestellfortschritt",
  "bookingSteps": [
    {
      "name": "Tickets",
      "route": "booking-de"
    },
    {
      "name": "Gutscheine",
      "route": "giftcards-de"
    },
    {
      "name": "Erlebnisse",
      "route": "booking-tours-de"
    },
    {
      "name": "Übersicht",
      "route": "booking-overview-de"
    },
    {
      "name": "Persönliche Daten",
      "route": "booking-personal-information-de"
    },
    {
      "name": "Bezahlung"
    },
    {
      "name": "Bestätigung",
      "route": "booking-confirmation-de"
    }
  ],
  "reservationSteps": [
    {
      "name": "Tickets",
      "route": "reservation-de"
    },
    {
      "name": "Übersicht",
      "route": "reservation-overview-de"
    },
    {
      "name": "Persönliche Daten",
      "route": "reservation-personal-information-de"
    },
    {
      "name": "Bestätigung",
      "route": "reservation-confirmation-de"
    }
  ],
  "completed": "Schritt abgeschlossen:"
}
</i18n>

<style lang="scss">
@import "../css/variables";
@import "../css/functions";
@import "../css/mixins";

.checkout-progress {
  @include vertical-spacing;

  list-style: none;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: $gutter-sm;

  background: #fff;

  @media (min-width: $breakpoint-md) {
    padding: $gutter-md;
  }
}

.checkout-progress__item {
  width: 100%;
  flex-shrink: 1;
  margin: 0;
}

.checkout-progress__badge {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 $gutter-xs;

  text-align: center;
  text-decoration: none;
  color: $color-strong;

  &::after {
    content: "";

    position: absolute;
    width: 100%;
    height: 0.25em;
    top: 0.625em;
    left: 50%;

    background-image: svg-url('<svg fill="#{$color-strong}" viewBox="0 0 48 24" width="48" height="24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="11"/></svg>');
    background-size: auto 100%;
  }

  &.is-active {
    color: $color-primary;
  }

  &.is-completed {
    color: $color-secondary-dark;

    &::after {
      height: 0.1875em;
      background: currentColor;
    }
  }

  &.is-link:hover {
    text-decoration: underline;
  }

  .checkout-progress__item:last-child &::after {
    display: none;
  }

  @media (min-width: $breakpoint-sm) {
    padding: 0 $gutter-sm;
  }

  @media (min-width: $breakpoint-md) {
    &::after {
      height: 0.375em;
      top: 0.9375em;
    }

    &.is-completed::after {
      top: 1em;
      height: 0.25em;
    }
  }

  @media (min-width: $breakpoint-lg) {
    padding: 0;
  }
}

.checkout-progress__number {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.5em;
  height: 1.5em;

  z-index: 1;

  font-weight: 700;
  color: #fff;

  background-color: $color-strong;
  box-shadow: 0 0 0 0.25em #fff;
  border-radius: 50%;

  .is-active & {
    background-color: $color-primary-dark;
  }

  .is-completed & {
    background-color: $color-secondary-dark;
  }

  .checkout-progress__item:last-child .is-completed & {
    font-size: 1.5em;
  }

  @media (min-width: $breakpoint-md) {
    font-size: 1.5em;

    .checkout-progress__item:last-child .is-completed & {
      font-size: 2em;
    }
  }

  @media (min-width: $breakpoint-lg) {
    .checkout-progress__item:last-child .is-completed & {
      top: -0.125em;
    }
  }
}

.checkout-progress__name {
  display: none;
  margin-top: 0.5em;

  font-size: 0.875em;
  font-weight: 600;

  @media (min-width: $breakpoint-lg) {
    display: block;
  }
}
</style>
